import { Facility, Target } from "@app/shared";
import React, { useId, useMemo } from "react";
import {
    buildFovConePath,
    CABLE_IMG_H,
    CABLE_IMG_W,
    computeLayoutPositions,
    DEFAULT_LENGTH,
    DEFAULT_WIDTH,
    facilityToSvgPoint,
    findAttenuatorZone,
    findSidePassZone,
    getDimensionsFromZone,
    ROAD_HEIGHT,
    ROAD_WIDTH,
    TRUCK_H,
    TRUCK_W,
    zoneToSvgRect,
} from "./attenuatorLayoutHelpers";

/** Side-pass zones get their own colour so the three alert areas read apart at a glance. */
const SIDE_PASS_COLOR = "#06B6D4";

type AttenuatorRoadViewProps = {
    facility: Facility;
    targets?: Target[];
    widthOverride?: number;
    lengthOverride?: number;
    showLegend?: boolean;
    zoomed?: boolean;
    className?: string;
};

export const AttenuatorRoadView: React.FC<AttenuatorRoadViewProps> = ({
    facility,
    targets,
    widthOverride,
    lengthOverride,
    showLegend = true,
    zoomed = false,
    className,
}) => {
    const markerId = useId();
    const arrowId = `target-arrow-${markerId}`;
    const arrowCasingId = `target-arrow-casing-${markerId}`;

    const zone = findAttenuatorZone(facility);
    const dims = zone ? getDimensionsFromZone(zone) : { width: DEFAULT_WIDTH, length: DEFAULT_LENGTH };

    const width = widthOverride ?? dims.width;
    const length = lengthOverride ?? dims.length;

    // The truck's whole alert geometry, not just the cushion: a director looking at this view is
    // asking "what is this unit watching", and the left/right pass zones are half of the answer.
    const sidePassRects = useMemo(
        () =>
            (["left", "right"] as const)
                .map((side) => {
                    const rect = zoneToSvgRect(findSidePassZone(facility, side)?.points);
                    return rect ? { side, rect } : undefined;
                })
                .filter(Boolean) as { side: "left" | "right"; rect: NonNullable<ReturnType<typeof zoneToSvgRect>> }[],
        [facility],
    );

    const device = facility.devices?.[0];
    // Always centred: traffic is assumed on both sides, which is the only assumption that is safe
    // for every truck and the only one operators read the same way.
    const positions = useMemo(() => computeLayoutPositions(width, length), [width, length]);

    const deviceFov = useMemo(() => {
        if (!device?.height || !device?.desiredTiltDegrees) return null;
        const devX = positions.cx;
        const devY = positions.truckY;
        const fovPath = buildFovConePath(devX, devY, device.height, device.desiredTiltDegrees);
        return { devX, devY, fovPath, color: device.color ?? "#3B82F6" };
    }, [device?.height, device?.desiredTiltDegrees, device?.color, positions.cx, positions.truckY]);

    const viewBox = useMemo(() => {
        if (!zoomed) return `0 0 ${ROAD_WIDTH} ${ROAD_HEIGHT}`;
        const pad = 60;
        const top = Math.max(0, positions.cushionY - 600);
        const bottom = Math.min(ROAD_HEIGHT, positions.truckY + TRUCK_H + pad + 50);
        return `0 ${top} ${ROAD_WIDTH} ${bottom - top}`;
    }, [zoomed, positions.cushionY, positions.truckY]);

    const targetPolylines = useMemo(() => {
        if (!targets?.length || !device || device.positionX == null || device.positionY == null || device.headingDegrees == null) return [];
        const devSvgX = positions.cx;
        const devSvgY = positions.truckY;
        const dev = { positionX: device.positionX, positionY: device.positionY, headingDegrees: device.headingDegrees };

        return targets
            .map((t) => {
                let points: { x: number; y: number }[] = [];
                if (t.history && t.history.length >= 2) {
                    points = [...t.history]
                        .sort((a, b) => (a.date?.getTime() ?? 0) - (b.date?.getTime() ?? 0))
                        .filter((h) => h.position)
                        .map((h) => facilityToSvgPoint({ x: h.position!.x ?? 0, y: h.position!.y ?? 0 }, dev, devSvgX, devSvgY));
                } else if (t.startPosition && t.endPosition) {
                    points = [
                        facilityToSvgPoint({ x: t.startPosition.x ?? 0, y: t.startPosition.y ?? 0 }, dev, devSvgX, devSvgY),
                        facilityToSvgPoint({ x: t.endPosition.x ?? 0, y: t.endPosition.y ?? 0 }, dev, devSvgX, devSvgY),
                    ];
                }
                if (points.length < 2) return null;
                // Trim the tail so the drawn line ends at the arrowhead's base; the arrow itself spans the
                // last ARROW_LEN units up to the true final point. Without this the thin line pokes out past
                // the narrow point of the arrowhead. ARROW_LEN matches the marker triangle's base→tip length.
                const ARROW_LEN = 38;
                const last = points[points.length - 1];
                const beforeLast = points[points.length - 2];
                const tdx = last.x - beforeLast.x;
                const tdy = last.y - beforeLast.y;
                const tlen = Math.hypot(tdx, tdy) || 1;
                const trim = Math.min(ARROW_LEN, tlen * 0.9);
                const lineEnd = { x: last.x - (tdx / tlen) * trim, y: last.y - (tdy / tlen) * trim };
                const linePoints = [...points.slice(0, -1), lineEnd];
                // Casing line is a slightly wider dark copy drawn underneath. Extend it a few units
                // back past the first point so the start of the path also gets an outline ("back" casing).
                const p0 = linePoints[0];
                const p1 = linePoints[1];
                const dx = p1.x - p0.x;
                const dy = p1.y - p0.y;
                const len = Math.hypot(dx, dy) || 1;
                const startCapExt = 3;
                const startCap = { x: p0.x - (dx / len) * startCapExt, y: p0.y - (dy / len) * startCapExt };
                const toStr = (pts: { x: number; y: number }[]) => pts.map((p) => `${p.x},${p.y}`).join(" ");
                return { id: t.id, pointsStr: toStr(linePoints), casingPointsStr: toStr([startCap, ...linePoints]) };
            })
            .filter(Boolean) as { id: string; pointsStr: string; casingPointsStr: string }[];
    }, [targets, device, positions.cx, positions.truckY]);

    return (
        <div className={className ?? "h-full flex flex-col items-center justify-center"}>
            <svg viewBox={viewBox} className="flex-1 min-h-0 w-auto" preserveAspectRatio="xMidYMid meet">
                <defs>
                    {/* Dark arrow: same triangle as the magenta one, but with a dark stroke that expands it
                        uniformly by ~2.5u on every side — this is the arrowhead's casing. Drawn under the magenta. */}
                    <marker id={arrowCasingId} viewBox="0 0 48 48" refX="6" refY="24" markerWidth="48" markerHeight="48" markerUnits="userSpaceOnUse" orient="auto-start-reverse" overflow="visible">
                        <path d="M 2 6 L 44 24 L 2 42 z" fill="#1A1A1A" stroke="#1A1A1A" strokeWidth={5} strokeLinejoin="round" />
                    </marker>
                    {/* Magenta arrow: solid fill, no stroke. Its base (x=2) is pulled a few units behind the
                        anchor (refX=6) so it overlaps the trimmed line end — the magenta reads as one
                        uninterrupted shape with no seam, and the dark only ever shows on the outer rim. */}
                    <marker id={arrowId} viewBox="0 0 48 48" refX="6" refY="24" markerWidth="48" markerHeight="48" markerUnits="userSpaceOnUse" orient="auto-start-reverse" overflow="visible">
                        <path d="M 2 6 L 44 24 L 2 42 z" fill="#FF1493" />
                    </marker>
                </defs>
                <image href="/attenuator/at_road_2.png" x={0} y={0} width={ROAD_WIDTH} height={ROAD_HEIGHT} />
                {deviceFov && (
                    <path
                        d={deviceFov.fovPath}
                        fill={deviceFov.color}
                        fillOpacity={0.3}
                        stroke={deviceFov.color}
                        strokeWidth={3}
                        strokeOpacity={0.7}
                    />
                )}
                {targetPolylines.map((t) => (
                    <g key={t.id}>
                        <polyline
                            points={t.casingPointsStr}
                            fill="none"
                            stroke="#1A1A1A"
                            strokeWidth={13}
                            strokeLinecap="butt"
                            strokeLinejoin="round"
                            markerEnd={`url(#${arrowCasingId})`}
                        />
                        <polyline
                            points={t.pointsStr}
                            fill="none"
                            stroke="#FF1493"
                            strokeWidth={8}
                            strokeLinecap="butt"
                            strokeLinejoin="round"
                            opacity={1}
                            markerEnd={`url(#${arrowId})`}
                        />
                    </g>
                ))}
                {sidePassRects.map(({ side, rect }) => (
                    <g key={side}>
                        <rect
                            x={rect.x}
                            y={rect.y}
                            width={rect.width}
                            height={rect.height}
                            fill="rgba(6, 182, 212, 0.12)"
                            stroke={SIDE_PASS_COLOR}
                            strokeWidth={3}
                            strokeDasharray="10 5"
                        />
                        <text
                            x={rect.x + rect.width / 2}
                            y={rect.y + 34}
                            textAnchor="middle"
                            fill={SIDE_PASS_COLOR}
                            fontSize={24}
                            fontWeight="bold"
                        >
                            {side === "left" ? "Left pass" : "Right pass"}
                        </text>
                    </g>
                ))}
                <rect
                    x={positions.cushionX}
                    y={positions.cushionY}
                    width={positions.cushionW}
                    height={positions.cushionH}
                    fill="rgba(234, 179, 8, 0.15)"
                    stroke="#EAB308"
                    strokeWidth={3}
                    strokeDasharray="10 5"
                />
                <image href="/attenuator/at_tile_module.png" x={positions.cushionX} y={positions.cushionY} width={positions.cushionW} height={positions.cushionH} preserveAspectRatio="none" />
                <image
                    href="/attenuator/at_cable.png"
                    x={positions.cableX}
                    y={positions.cableY}
                    width={CABLE_IMG_W}
                    height={CABLE_IMG_H}
                    transform={`rotate(90, ${positions.cableX + CABLE_IMG_W / 2}, ${positions.cableY + CABLE_IMG_H / 2})`}
                />
                <image
                    href="/attenuator/at_truck.png"
                    x={positions.truckX}
                    y={positions.truckY}
                    width={TRUCK_W}
                    height={TRUCK_H}
                    transform={`rotate(180, ${positions.truckX + TRUCK_W / 2}, ${positions.truckY + TRUCK_H / 2})`}
                />
                {deviceFov && <circle cx={deviceFov.devX} cy={deviceFov.devY} r={8} fill={deviceFov.color} stroke="white" strokeWidth={2} />}
                <g opacity={0.5}>
                    <text x={positions.cx} y={ROAD_HEIGHT - 30} textAnchor="middle" fill="white" fontSize={28} fontWeight="bold">
                        ↓ Travel Direction
                    </text>
                </g>
            </svg>

            {showLegend && (
                <div className="w-full flex flex-wrap gap-x-5 gap-y-1 pt-2 border-t border-gray-200 text-xs text-gray-600">
                    <div className="flex items-center gap-1.5">
                        <span className="inline-block w-4 h-3 rounded-sm border-2 border-dashed" style={{ borderColor: "#EAB308", backgroundColor: "rgba(234, 179, 8, 0.15)" }} />
                        Protected Zone (impending collision)
                    </div>
                    {sidePassRects.length > 0 && (
                        <div className="flex items-center gap-1.5">
                            <span className="inline-block w-4 h-3 rounded-sm border-2 border-dashed" style={{ borderColor: SIDE_PASS_COLOR, backgroundColor: "rgba(6, 182, 212, 0.12)" }} />
                            Side Pass Zone
                        </div>
                    )}
                    {deviceFov && (
                        <div className="flex items-center gap-1.5">
                            <span className="inline-block w-4 h-3 rounded-sm" style={{ backgroundColor: deviceFov.color, opacity: 0.5 }} />
                            Device Field of View
                        </div>
                    )}
                    {targetPolylines.length > 0 && (
                        <div className="flex items-center gap-1.5">
                            <span className="inline-block w-4 h-0.5 rounded" style={{ backgroundColor: "#EF4444" }} />
                            Target Trajectory
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
